<header class="flex header">
  <h2>
    @if (isExistingLine()) {
      {{ 'CashRequest_Line_EditTitle' | translateWF: 'Entries on this account' }}
    } @else {
      {{ 'CashRequest_Line_AddTitle' | translateWF: 'Add a line' }}
    }
  </h2>
  <p-button icon="pi pi-times" (click)="onClose()" />
</header>

<section class="content">
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <form [formGroup]="form">
    <div class="grid-12">
      <!-- The account IS the line. Fixed once documents have been routed here — changing it would file
           them under an account their own source never touched. -->
      <div class="span-12-6">
        <app-financial-account-select
          [referenceControl]="form.controls.glAccountReference"
          [numberControl]="form.controls.glAccountNumber"
          [nameControl]="form.controls.glAccountName"
          [label]="'CashRequest_Account' | translateWF: 'Account'"
          [placeholder]="'CashRequest_Line_PickAccount' | translateWF: 'Select the account this is planned against'"
          [required]="true"
          [readonly]="isExistingLine()"
          [postingModule]="postingModule"
        />
      </div>

      <div class="span-12-6">
        <div class="form-box">
          <label>{{ 'CashRequest_LineDesc' | translateWF: 'Description' }}</label>
          <input
            pInputText
            formControlName="description"
            class="w-full"
            [readonly]="isExistingLine()"
            [placeholder]="'CashRequest_Line_DescHint' | translateWF: 'What this account covers this week'"
          />
          @if (isExistingLine()) {
            <small class="text-surface-500">{{ 'CashRequest_Line_DescOnRow' | translateWF: 'Edit the description on the line itself, then save the request.' }}</small>
          }
        </div>
      </div>
    </div>

    <!-- What the money is for. One row per obligation, so a week with three planillas reads as three
         things rather than one lump sum nobody can take apart. -->
    <div class="mt-4">
      <div class="row-between-center mb-2">
        <div class="col">
          <span class="text-base font-semibold">{{ 'CashRequest_Line_Entries' | translateWF: 'Manual entries' }}</span>
          <small class="text-surface-500">{{
            'CashRequest_Line_Entries_Hint' | translateWF: 'Planillas, taxes and anything else with no document behind it. Amounts are USD.'
          }}</small>
        </div>
        <p-button [label]="'CashRequest_Line_AddEntry' | translateWF: 'Add entry'" icon="pi pi-plus" size="small" variant="outlined" (click)="addEntry()" />
      </div>

      <p-table [value]="entries.controls" styleClass="p-datatable-sm">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ 'CashRequest_Line_EntryDesc' | translateWF: "What it's for" }}</th>
            <th class="text-right" style="width: 14rem">{{ 'CashRequest_Usd' | translateWF: 'USD' }}</th>
            <th style="width: 4rem"></th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-entry let-index="rowIndex">
          <tr [formGroup]="entry">
            <td>
              <input
                pInputText
                formControlName="description"
                class="w-full"
                [placeholder]="'CashRequest_Line_EntryDescHint' | translateWF: 'e.g. Planilla quincenal — administración'"
                [ngClass]="{ 'ng-invalid ng-dirty': entry.controls.description.invalid && entry.controls.description.touched }"
              />
            </td>
            <td>
              <p-inputNumber
                formControlName="amount"
                mode="decimal"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                [min]="0"
                styleClass="w-full"
                inputStyleClass="text-right"
                [ngClass]="{ 'ng-invalid ng-dirty': entry.controls.amount.invalid && entry.controls.amount.touched }"
              />
            </td>
            <td class="text-center">
              <p-button
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                [rounded]="true"
                [pTooltip]="'CashRequest_Line_RemoveEntry' | translateWF: 'Remove this entry'"
                (click)="removeEntry(index)"
              />
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="3" class="text-center py-6 text-[var(--p-text-muted-color)]">
              @if (wouldEmptyLine()) {
                {{ 'CashRequest_Line_WouldEmpty' | translateWF: 'Removing every entry would leave an empty line — delete the line instead.' }}
              } @else {
                {{ 'CashRequest_Line_NoEntries' | translateWF: 'Add at least one entry.' }}
              }
            </td>
          </tr>
        </ng-template>
      </p-table>

      <div class="row-end-stretch gap-6 mt-3">
        <!-- Named separately: what documents already brought is not what this screen is changing. -->
        @if (documentTotal() > 0) {
          <span class="text-sm text-[var(--p-text-muted-color)]">
            {{ 'CashRequest_Documents' | translateWF: 'Documents' }}
            <span class="tabular-nums">{{ documentTotal() | number: '1.2-2' }}</span>
          </span>
        }
        <span class="text-sm text-[var(--p-text-muted-color)]">
          {{ 'CashRequest_Manual' | translateWF: 'Manual' }}
          <span class="tabular-nums">{{ manualTotal() | number: '1.2-2' }}</span>
        </span>
        <span class="font-semibold">
          {{ 'CashRequest_Line_Total' | translateWF: 'Line total' }}
          <span class="tabular-nums">{{ lineTotal() | number: '1.2-2' }} USD</span>
        </span>
      </div>
    </div>
  </form>
</section>

<footer>
  <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="onClose()" />
  <p-button [label]="'Save' | translateWF: 'Save'" icon="pi pi-save" severity="success" (click)="onSave()" />
</footer>
